import React, { useState, useCallback } from "react";
import {
  Button,
  Input,
  Text,
  Card,
  RadioGroup,
  Radio,
  Spinner,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Mail24Regular,
  CheckmarkCircle24Regular,
  Search24Regular,
  Building24Regular,
  Briefcase24Regular,
  ArrowLeft24Regular,
  Person24Regular,
} from "@fluentui/react-icons";
import { useFileEmail } from "../../hooks/useFileEmail";
import { OutlookMailItem } from "../../services/outlookService";
import { dataverseClient } from "../../services/dataverseClient";

const useStyles = makeStyles({
  container: { display: "flex", flexDirection: "column", gap: "8px" },
  success: {
    color: tokens.colorStatusSuccessForeground1,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  error: { color: tokens.colorStatusDangerForeground1 },
  searchRow: { display: "flex", gap: "4px", alignItems: "flex-end" },
  resultCard: {
    padding: "8px",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedCard: {
    padding: "8px",
    borderColor: tokens.colorBrandStroke1,
    borderWidth: "2px",
  },
  resultIcon: { display: "flex", alignItems: "center", gap: "6px" },
  resultMeta: { color: tokens.colorNeutralForeground3, fontSize: "12px" },
});

interface FileEmailButtonProps {
  mailItem: OutlookMailItem;
  contacts: any[];
  leads: any[];
}

type SearchCategory = "account" | "opportunity";

interface SearchResult {
  id: string;
  name: string;
  type: SearchCategory;
  detail: string;
}

export const FileEmailButton: React.FC<FileEmailButtonProps> = ({ mailItem, contacts }) => {
  const styles = useStyles();
  const { fileEmail, filing, success, error } = useFileEmail();

  const [step, setStep] = useState<"initial" | "search" | "confirm">("initial");
  const [filedTo, setFiledTo] = useState<string | null>(null);
  const [category, setCategory] = useState<SearchCategory>("account");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SearchResult | null>(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError(null);
    setResults([]);

    try {
      if (category === "account") {
        const accounts = await dataverseClient.searchAccounts(query.trim());
        setResults(
          accounts.map((a: any) => ({
            id: a.accountid,
            name: a.name,
            type: "account" as SearchCategory,
            detail: [a.accountnumber, a.emailaddress1, a.telephone1]
              .filter(Boolean)
              .join(" | "),
          }))
        );
      } else {
        const opps = await dataverseClient.searchOpportunities(query.trim());
        setResults(
          opps.map((o: any) => ({
            id: o.opportunityid,
            name: o.name,
            type: "opportunity" as SearchCategory,
            detail: o.estimatedvalue
              ? `Est. value: $${Number(o.estimatedvalue).toLocaleString()}`
              : "",
          }))
        );
      }
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setSearching(false);
    }
  }, [query, category]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSelect = (result: SearchResult) => {
    setSelected(result);
    setStep("confirm");
  };

  const handleFile = () => {
    if (!selected) return;
    setFiledTo(selected.name);
    fileEmail(mailItem, selected.id, selected.type);
  };

  const handleFileToContact = (contact: any) => {
    setFiledTo(contact.fullname);
    fileEmail(mailItem, contact.contactid, "contact");
  };

  const handleReset = () => {
    setStep("initial");
    setQuery("");
    setResults([]);
    setSelected(null);
    setSearchError(null);
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.success}>
          <CheckmarkCircle24Regular />
          <Text size={200}>
            Email filed to {filedTo || "CRM"} successfully.
          </Text>
        </div>
        <Button appearance="subtle" onClick={handleReset} size="small">
          File another
        </Button>
      </div>
    );
  }

  if (step === "initial") {
    return (
      <div className={styles.container}>
        {contacts.length > 0 && contacts.map((c: any) => (
          <Button
            key={c.contactid}
            icon={<Person24Regular />}
            appearance="primary"
            onClick={() => handleFileToContact(c)}
            disabled={filing}
          >
            {filing ? "Filing..." : `File to ${c.fullname}'s Timeline`}
          </Button>
        ))}
        <Button
          icon={<Search24Regular />}
          appearance="outline"
          onClick={() => setStep("search")}
        >
          File to Account or Opportunity
        </Button>
        {error && (
          <Text size={200} className={styles.error}>{error.message}</Text>
        )}
      </div>
    );
  }

  if (step === "confirm" && selected) {
    return (
      <div className={styles.container}>
        <Button
          icon={<ArrowLeft24Regular />}
          appearance="subtle"
          size="small"
          onClick={() => setStep("search")}
        >
          Back to search
        </Button>

        <Card className={styles.selectedCard}>
          <div className={styles.resultIcon}>
            {selected.type === "account" ? (
              <Building24Regular />
            ) : (
              <Briefcase24Regular />
            )}
            <div>
              <Text weight="semibold" block>
                {selected.name}
              </Text>
              <Text size={200} className={styles.resultMeta}>
                {selected.type === "account" ? "Account" : "Opportunity"}
                {selected.detail && ` — ${selected.detail}`}
              </Text>
            </div>
          </div>
        </Card>

        <Button
          icon={<Mail24Regular />}
          appearance="primary"
          onClick={handleFile}
          disabled={filing}
        >
          {filing ? "Filing..." : `File to ${selected.name}`}
        </Button>

        {error && (
          <Text size={200} className={styles.error}>
            {error.message}
          </Text>
        )}
      </div>
    );
  }

  // Search step
  return (
    <div className={styles.container}>
      <Button
        icon={<ArrowLeft24Regular />}
        appearance="subtle"
        size="small"
        onClick={handleReset}
      >
        Cancel
      </Button>

      <RadioGroup
        layout="horizontal"
        value={category}
        onChange={(_, data) => {
          setCategory(data.value as SearchCategory);
          setResults([]);
        }}
      >
        <Radio value="account" label="Account" />
        <Radio value="opportunity" label="Opportunity" />
      </RadioGroup>

      <div className={styles.searchRow}>
        <Input
          placeholder={
            category === "account"
              ? "Search by name, email, or account #"
              : "Search by opportunity name"
          }
          value={query}
          onChange={(_, data) => setQuery(data.value)}
          onKeyDown={handleKeyDown}
          style={{ flex: 1 }}
        />
        <Button
          icon={<Search24Regular />}
          onClick={handleSearch}
          disabled={searching || !query.trim()}
        />
      </div>

      {searching && <Spinner size="tiny" label="Searching..." />}

      {searchError && (
        <Text size={200} className={styles.error}>
          {searchError}
        </Text>
      )}

      {!searching && results.length === 0 && query && !searchError && (
        <Text size={200}>No results found.</Text>
      )}

      {results.map((r) => (
        <Card
          key={r.id}
          className={styles.resultCard}
          onClick={() => handleSelect(r)}
        >
          <div className={styles.resultIcon}>
            {r.type === "account" ? (
              <Building24Regular />
            ) : (
              <Briefcase24Regular />
            )}
            <div>
              <Text weight="semibold" block>
                {r.name}
              </Text>
              {r.detail && (
                <Text size={200} className={styles.resultMeta}>
                  {r.detail}
                </Text>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
